export const COMFORT_LEVELS = [
  { id: "beginner", label: "Beginner", emoji: "🌱" },
  { id: "some_experience", label: "Some experience", emoji: "💪" },
  { id: "advanced", label: "Advanced", emoji: "⛰️" },
] as const;

export const PREFERRED_DAYS = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
  { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
] as const;

export const PREFERRED_TIMES = [
  { id: "morning", label: "Morning", emoji: "🌅" },
  { id: "afternoon", label: "Afternoon", emoji: "☀️" },
  { id: "evening", label: "Evening", emoji: "🌙" },
] as const;

export const PRICE_PREFERENCES = [
  { id: "free_only", label: "Free events only" },
  { id: "free_and_paid", label: "Free and paid events" },
] as const;

export const SA_NEIGHBORHOODS = [
  "Downtown",
  "Alamo Heights",
  "Stone Oak",
  "Southtown",
  "Medical Center",
  "The Pearl",
  "Northwest Side",
  "Northeast Side",
  "West Side",
  "South Side",
  "Boerne / Hill Country",
  "New Braunfels",
];

/** Normalizes a jsonb array column that may arrive as null or a non-array. */
export const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

export interface EventPreferences {
  neighborhood?: string | null;
  preferred_days?: unknown;
  preferred_times?: unknown;
  price_preference?: string | null;
  comfort_level?: string | null;
}

const DAY_IDS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/**
 * Scores how well an event matches a user's stated preferences.
 * Purely a display-ordering signal — nothing is filtered out.
 */
export const scoreEventForPreferences = (
  event: {
    date: string;
    location?: string | null;
    time_of_day?: string | null;
    is_free?: boolean | null;
    level?: string | null;
  },
  prefs?: EventPreferences | null
): number => {
  if (!prefs) return 0;
  let score = 0;

  const days = toStringArray(prefs.preferred_days);
  if (days.length) {
    const eventDay = new Date(event.date)
      .toLocaleDateString("en-US", { timeZone: "America/Chicago", weekday: "short" })
      .toLowerCase();
    if (days.includes(eventDay)) score += 3;
  }

  const times = toStringArray(prefs.preferred_times);
  if (times.length && event.time_of_day && times.includes(event.time_of_day)) {
    score += 2;
  }

  if (prefs.neighborhood && event.location) {
    if (event.location.toLowerCase().includes(prefs.neighborhood.toLowerCase())) {
      score += 3;
    }
  }

  if (prefs.price_preference === "free_only" && event.is_free) score += 2;

  if (prefs.comfort_level === "beginner" && event.level?.toLowerCase().includes("beginner")) {
    score += 1;
  }

  return score;
};
